import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";

/** We're visually at the bottom as long as we scrolled past the whitespace below the last message bubble. */
const scrollToBottomVisualTolerance = 16;

/** Some measurements are based on device pixels, while others are rounded to the nearest logical pixel. */
const scrollToBottomRoundingTolerance = 1;

export class ChatScrollState {
  readonly #containerElement: HTMLElement;
  readonly #clientHeight: number;
  readonly #scrollHeight: number;
  #scrollTop: number;

  constructor(containerElement: HTMLElement) {
    this.#containerElement = containerElement;
    this.#clientHeight = containerElement.clientHeight;
    this.#scrollHeight = containerElement.scrollHeight;
    this.#scrollTop = containerElement.scrollTop;
  }

  get maxScrollTop(): number {
    return this.#scrollHeight - this.#clientHeight;
  }

  get isVisuallyScrolledToBottom(): boolean {
    return (
      this.#scrollTop >=
      this.maxScrollTop -
        scrollToBottomRoundingTolerance -
        scrollToBottomVisualTolerance
    );
  }

  get isScrolledToBottom(): boolean {
    return (
      this.#scrollTop >= this.maxScrollTop - scrollToBottomRoundingTolerance
    );
  }

  scrollToBottom(): void {
    if (!this.isScrolledToBottom) {
      this.#containerElement.scrollTop = this.maxScrollTop;
      this.#scrollTop = this.#containerElement.scrollTop;
    }
  }

  checkDidResizeSince(prevScrollState: ChatScrollState): boolean {
    return (
      this.#clientHeight !== prevScrollState.#clientHeight ||
      this.#scrollHeight !== prevScrollState.#scrollHeight
    );
  }

  checkDidScrollSince(prevScrollState: ChatScrollState): boolean {
    return (
      !this.checkDidResizeSince(prevScrollState) &&
      this.#scrollTop !== prevScrollState.#scrollTop
    );
  }
}

export function useChatScroll(
  scrollContainerRef: RefObject<HTMLElement | null>,
  scrollContentRef: RefObject<HTMLElement | null>,
  options?: { isSuspended?: boolean },
) {
  const isSuspended = options?.isSuspended ?? false;
  const isSuspendedRef = useRef(isSuspended);
  const hasPendingScrollToBottomRef = useRef(false);

  useLayoutEffect(() => {
    const containerElement = scrollContainerRef.current;
    const contentElement = scrollContentRef.current;
    if (!containerElement || !contentElement) {
      return;
    }

    let prevScrollState = new ChatScrollState(containerElement);

    const resizeObserver = new ResizeObserver(() => {
      const scrollState = new ChatScrollState(containerElement);

      // If either the container or the content resized such that we're no longer scrolled to the bottom, update the scroll
      if (
        prevScrollState.isVisuallyScrolledToBottom &&
        !scrollState.isScrolledToBottom
      ) {
        if (isSuspendedRef.current) {
          hasPendingScrollToBottomRef.current = true;
        } else {
          scrollState.scrollToBottom();
        }
      }

      prevScrollState = scrollState;
    });

    const handleScroll = () => {
      const scrollState = new ChatScrollState(containerElement);

      // If this is a user-initiated scroll, update the scroll state so that we know we're no longer scrolled to the bottom
      if (scrollState.checkDidScrollSince(prevScrollState)) {
        prevScrollState = scrollState;
      }
    };

    resizeObserver.observe(containerElement);
    resizeObserver.observe(contentElement);
    containerElement.addEventListener("scroll", handleScroll);

    return () => {
      resizeObserver.disconnect();
      containerElement.removeEventListener("scroll", handleScroll);
    };
  }, [scrollContainerRef, scrollContentRef]);

  const scrollToBottom = useCallback(() => {
    const containerElement = scrollContainerRef.current;
    if (!containerElement) {
      return;
    }

    const scrollState = new ChatScrollState(containerElement);
    scrollState.scrollToBottom();
  }, [scrollContainerRef]);

  useEffect(() => {
    isSuspendedRef.current = isSuspended;
    if (isSuspended || !hasPendingScrollToBottomRef.current) {
      return;
    }
    hasPendingScrollToBottomRef.current = false;
    scrollToBottom();
  }, [isSuspended, scrollContainerRef, scrollToBottom]);

  return {
    scrollToBottom,
  };
}
